/**
 * The rules behind the path editor, with no React in them.
 *
 * Its own module because CRA's jest cannot resolve react-router-dom v7 or
 * react-dnd, so anything sharing a file with a component that imports them is
 * untestable. These rules decide what URL a page publishes at, which is not
 * something to leave uncovered.
 *
 * Named `pathRules` rather than `pathChips` because macOS is case-insensitive:
 * `pathChips.ts` beside `PathChips.tsx` is one file to the filesystem and two
 * to TypeScript, and the error it produces names neither.
 */

/**
 * Turn typed text into a URL-safe segment.
 *
 * MUST AGREE WITH THE SERVER. `_clean_path` in `api/managed_pages.py` slugifies
 * every segment before storing it, so a client that shows the raw text is
 * showing a preview of a URL that will not be the one that ships. Same rules,
 * applied as you type, so what you see is what is stored.
 */
export function slugifySegment(raw: string): string {
  // A LINE-BY-LINE PORT of `slugify` in `database/utils.py`, in the same order.
  // Written out rather than approximated because the two only have to disagree
  // once for someone to be shown one URL and given another.
  //
  // `\p{L}\p{N}` stands in for Python's `\w`, which is unicode-aware — JavaScript's
  // `\w` is ASCII-only, so the obvious `[^a-z0-9]` turns "Über" into "ber" here
  // while the server stores "über".
  return raw
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}_\s-]/gu, '') // drop non-word chars (keep hyphens)
    .replace(/[\s_]+/gu, '-') // spaces/underscores → hyphen
    .replace(/-+/g, '-') // collapse consecutive hyphens
    .replace(/^-+|-+$/g, '');
}

/**
 * Split pasted text into segments.
 *
 * People paste `bikes/trails/` or a whole URL rather than typing one segment at
 * a time, and a single chip reading "bikes-trails" is a wrong URL that looks
 * deliberate. Empty pieces are dropped, so a leading or trailing slash costs
 * nothing.
 */
export function splitPasted(raw: string): string[] {
  return raw
    .split('/')
    .map(slugifySegment)
    .filter(Boolean);
}

/** `/a/b/` — how the path reads as a URL. `/` when there are no segments. */
export function formatPath(segments: string[]): string {
  const clean = segments.filter(Boolean);
  return clean.length ? `/${clean.join('/')}/` : '/';
}

/**
 * The full public path of one page, for the preview line.
 *
 * Flat sites publish every page at the root regardless of the path — mirroring
 * `NESTED_SITE_TYPES` in `services/page_urls.py`. Showing a nested preview for
 * a WordPress site would promise a URL the site never serves.
 */
export function previewPath(
  segments: string[],
  pageSlug: string,
  nested: boolean,
): string {
  const parts = nested ? [...segments, pageSlug] : [pageSlug];
  return formatPath(parts);
}

/** Site types whose pages publish under their path. Mirrors NESTED_SITE_TYPES. */
export function isNestedSiteType(siteType: string | null | undefined): boolean {
  return siteType === 'HUGO';
}

/**
 * Which suggestions to show for what has been typed so far.
 *
 * Already-used segments stay in the list rather than being filtered out: the
 * whole point is to steer someone onto an existing branch, and hiding the one
 * they are half-way through typing is how they end up inventing a near-duplicate.
 * Matching is on the SLUGIFIED input, so "Types of" finds "types-of-ebikes".
 */
export function matchSuggestions(
  suggestions: { segment: string; page_count: number }[],
  typed: string,
  limit = 8,
): { segment: string; page_count: number }[] {
  const needle = slugifySegment(typed);
  const pool = needle
    ? suggestions.filter((s) => s.segment.includes(needle))
    : suggestions;
  return pool.slice(0, limit);
}
